/* eslint-disable no-underscore-dangle */
/**
 *
 * SearchBar
 *
 */

import React, { useReducer, useRef, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import { useHistory } from 'react-router-dom';
import { makeStyles } from '@material-ui/core/styles';
import CircularProgress from '@material-ui/core/CircularProgress';
import CancelIcon from '@material-ui/icons/Cancel';
import AlgoliaClient from 'utils/algoliaService';
import { useSelector } from 'react-redux';
import MixPanel from 'utils/mixpanelService';
import _classNames from 'classnames';
import { getCookie } from 'utils/cookieStore';
import {
  makeSelectClientDetails,
  makeSelectSiteCopy,
} from 'containers/Main/selectors';
import Config from 'utils/getEnvConfig';
import _isEmpty from 'lodash/isEmpty';
import _get from 'lodash/get';
import _forEach from 'lodash/forEach';
import IconButton from '@material-ui/core/IconButton';
import EmbedSearchIcon from 'components/BackHeader/images/search.svg';
import Overlay from './components/Overlay';
import SearchIcon from './white-search.svg';
import CloseIcon from './close-search.svg';

const {
  ALGOLIA: { SEARCH_INDEX },
} = Config;

const searchIndex = AlgoliaClient.initIndex(SEARCH_INDEX);

const useStyles = makeStyles(theme => ({
  searchBox: {
    position: 'relative',
    background: '#ffffff',
    color: '#000',
    height: 45,
    borderRadius: 3,
    border: `1px solid #E3E3E3`,
    [theme.breakpoints.down('xs')]: {
      width: '100%',
      marginBottom: 8,
    },
  },
  flatBox: {
    border: 'none',
    borderBottom: '1px solid #E3E3E3',
    borderRadius: 0,
    marginBottom: 0,
  },
  cancelIcon: {
    position: 'absolute',
    height: '100%',
    right: 62,
    top: 0,
    color: '#D6D6D6',
    width: 16,
    cursor: 'pointer',
    '& path': {
      pointerEvents: 'none',
    },
  },
  iconContainer: {
    position: 'absolute',
    height: '100%',
    right: 0,
    top: 0,
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    cursor: 'pointer',
    [theme.breakpoints.up('600')]: {
      background: '#01619B',
      width: 54,
      borderTopRightRadius: 3,
      borderBottomRightRadius: 3,
    },
  },
  progress: {
    height: '20px !important',
    width: '20px !important',
    color: theme.palette.text.secondary,
    [theme.breakpoints.up('600')]: {
      color: 'white',
    },
  },
  input: {
    flex: '1',
    width: '100%',
    height: '100%',
    paddingLeft: 16,
    paddingRight: 82,
    fontWeight: 300,
    fontSize: '16px !important',
    cursor: 'text',
    border: 'none',
    borderRadius: 4,
    '-webkit-appearance': 'none',
    fontFamily: 'MadaRegular',
    color: '#4B4B4B',
    backgroundColor: 'transparent',
  },
  embedSearch: {
    fontSize: 16,
    background: 'transparent',
    borderBottom: 'none',
    height: 36,
    '& img': {
      marginLeft: 10,
      height: 16,
      width: 19,
    },
    '&:hover': {
      background: 'none',
    },
    '& input': {
      color: '#848484',
      fontSize: '14px !important',
      minWidth: 0,
      paddingRight: 20,
    },
  },
  embedIconContainer: {
    background: 'transparent',
    width: 35,
    ' & button:hover': {
      background: 'none',
    },
  },
  embedInput: {
    paddingRight: 37,
    paddingLeft: 0,
    minWidth: 200,
  },
}));

const fetchSuggestions = async (query, filters) => {
  if (query) {
    try {
      const content = await searchIndex.search(query, {
        hitsPerPage: 30,
        filters,
      });

      const suggestions = {
        topics: content.hits.filter(hit => hit.type === 'Topics'),
        assessments: content.hits.filter(hit => hit.type === 'Assessments'),
        resources: content.hits.filter(
          hit => hit.type !== 'Topics' && hit.type !== 'Assessments',
        ),
      };
      return suggestions;
    } catch (err) {
      return {};
    }
  }
  return {};
};

const INITIAL_STATE = {
  processing: false,
  query: '',
  data: {},
  focused: false,
  offsetTop: 0,
};

const ON_CHANGE = 'on-change';
const ON_ENTER_PRESSED = 'on-enter-pressed';
const ON_FETCH_END = 'on-fetch-end';
const ON_CLICK_INSIDE = 'on-click-inside';
const ON_CLICK_AWAY = 'on-click-away';
const RESET = 'reset';

const reducer = (state, action) => {
  switch (action.type) {
    case ON_CHANGE:
      return {
        ...state,
        focused: true,
        processing: true,
        query: action.payload,
      };
    case ON_ENTER_PRESSED:
      return {
        data: {},
        focused: false,
        processing: false,
        query: '',
      };
    case ON_FETCH_END:
      return {
        ...state,
        data: action.payload,
        processing: false,
      };
    case ON_CLICK_INSIDE:
      return {
        ...state,
        focused: true,
        offsetTop: action.payload,
      };
    case ON_CLICK_AWAY:
      return {
        ...state,
        focused: false,
      };

    case RESET:
      return INITIAL_STATE;
    default:
      throw new Error();
  }
};

const SearchBar = ({ close, isFlat, isEmbedded }) => {
  const history = useHistory();
  const brand = getCookie('brand');
  const branded = !!brand && brand !== 'none';
  const classes = useStyles();
  const containerRef = useRef(null);
  const inputRef = useRef(null);
  const timeout = useRef(null);
  const [anchorEl, setAnchorEl] = useState(null);
  const [state, dispatch] = useReducer(reducer, INITIAL_STATE);

  const clientDetails = useSelector(makeSelectClientDetails());
  const siteCopy = useSelector(makeSelectSiteCopy());

  const filters = useMemo(() => {
    const clientExcludedTopic = _get(
      clientDetails,
      'excludeTopicCollection.items',
    );
    const excludedTopicTitle = !_isEmpty(clientExcludedTopic)
      ? clientExcludedTopic.map(topic => topic.title)
      : [];

    let filtersString = "clients:'none'";

    if (branded) {
      filtersString += ` OR clients:'${brand.toLowerCase()}' `;
    }

    if (!_isEmpty(excludedTopicTitle)) {
      _forEach(excludedTopicTitle, title => {
        filtersString += ` AND NOT nameTitle:'${title}' AND NOT relatedTopics:'${title}'`;
      });
    }

    return filtersString;
  }, [clientDetails]);

  const { data, focused, processing, query, offsetTop } = state;

  const searchConfig = (siteCopy || []).filter(
    config => config.slug === 'search',
  )[0];

  const onSuggestionPillClick = text => {
    MixPanel.track('User Search - Click Search Tag', {
      tag: text,
    });
    onChange(text);
  };

  const onResourceClick = clickData => {
    MixPanel.track('User Search - Select Result', {
      searchTerm: query,
      ...clickData,
    });
    dispatch({ type: RESET });
  };

  const onLinkClick = linkType => {
    MixPanel.track('User Search - Click Link', {
      searchTerm: query,
      type: linkType,
    });
    dispatch({ type: RESET });
  };

  const onFocus = event => {
    if (!anchorEl) setAnchorEl(event.currentTarget);

    let ot = 0;
    if (containerRef) {
      const viewportRect = containerRef.current.getBoundingClientRect();
      ot = viewportRect.top;
    }

    dispatch({ type: ON_CLICK_INSIDE, payload: ot });
    MixPanel.track('User Search - Focused');
  };

  const onChange = e => {
    const queryValue = typeof e === 'string' ? e : _get(e, 'target.value', '');
    dispatch({ type: ON_CHANGE, payload: queryValue });

    clearTimeout(timeout.current);

    timeout.current = setTimeout(async () => {
      if (typeof e !== 'string')
        MixPanel.track('User Search - Query', {
          searchTerm: queryValue,
        });

      const suggestions = await fetchSuggestions(queryValue, filters);
      dispatch({ type: ON_FETCH_END, payload: suggestions });
    }, 300);
  };

  const onKeyDown = e => {
    if (query !== '' && e.key === 'Enter') {
      MixPanel.track('User Search - Enter Pressed', {
        searchTerm: query,
      });
      dispatch({ type: ON_ENTER_PRESSED });
      if (isFlat) {
        close();
      }
      history.push(`/resources?query=${query}`);
    }
  };

  const onClickAway = e => {
    if (e.target.id !== 'search-input' && e.target.id !== 'search-clear')
      dispatch({ type: ON_CLICK_AWAY });
  };

  const onClearClicked = () => {
    onChange('');

    MixPanel.track('User Search - Clear Clicked', {
      searchTerm: query,
    });

    if (inputRef.current) inputRef.current.focus();
  };

  const onSearchClick = () => {
    MixPanel.track('User Search - Search Clicked', {
      searchTerm: query,
    });
    if (query === '') {
      if (inputRef.current) inputRef.current.focus();
    } else {
      history.push(`/resources?query=${query}`);
    }
  };

  const closeSearch = () => {
    close();
  };
  const inputProps = {
    placeholder: isEmbedded
      ? 'CredibleMind Search'
      : _get(
          searchConfig,
          'callToAction',
          'Search topics, resources and more...',
        ),
    value: query,
    onChange,
    onFocus,
    onKeyDown,
    'aria-label': 'Search Resources',
  };
  return (
    <div
      className={_classNames(classes.searchBox, {
        [classes.embedSearch]: isEmbedded,
        [classes.flatBox]: isFlat || isEmbedded,
      })}
      ref={containerRef}
    >
      <input
        {...inputProps}
        ref={inputRef}
        id="search-input"
        className={_classNames(classes.input, isEmbedded && classes.embedInput)}
        autoComplete="off"
      />
      {focused && (
        <Overlay
          anchorEl={anchorEl}
          data={data}
          focused={focused}
          offsetTop={offsetTop}
          onChange={onChange}
          onClickAway={onClickAway}
          onLinkClick={onLinkClick}
          onResourceClick={onResourceClick}
          onSuggestionPillClick={onSuggestionPillClick}
          query={query}
          searchConfig={searchConfig}
          isEmbedded={isEmbedded}
        />
      )}
      {query !== '' && focused && (
        <CancelIcon
          onClick={onClearClicked}
          className={classes.cancelIcon}
          id="search-clear"
        />
      )}
      <div
        className={_classNames(
          classes.iconContainer,
          isEmbedded && classes.embedIconContainer,
        )}
      >
        {processing ? (
          <CircularProgress className={classes.progress} />
        ) : (
          <>
            {isFlat || isEmbedded ? (
              <IconButton onClick={closeSearch} disableRipple>
                <img
                  src={isEmbedded ? EmbedSearchIcon : CloseIcon}
                  alt="Close"
                  className={classes.close}
                />
              </IconButton>
            ) : (
              <div role="button" onClick={onSearchClick} tabIndex={0}>
                <img
                  className={classes.searchIcon}
                  src={SearchIcon}
                  alt="Search Icon"
                />
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
};

SearchBar.propTypes = {
  history: PropTypes.object,
  callback: PropTypes.func,
  isFlat: PropTypes.bool,
  close: PropTypes.func,
  isEmbedded: PropTypes.bool,
};

export default SearchBar;
